import React, { createContext, useContext, useState, useCallback } from 'react';

const SessionContext = createContext();

const STORAGE_KEY = 'caisar-sessions';

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

function loadSessions() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function persistSessions(sessions) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
}

export function SessionProvider({ children }) {
  const [sessions, setSessions] = useState(loadSessions);

  const saveSession = useCallback((sessionData, status) => {
    setSessions((prev) => {
      const existing = prev.findIndex((s) => s.id === sessionData.id);
      const now = new Date().toISOString();
      const session = {
        id: sessionData.id || generateId(),
        name: sessionData.name,
        status,
        createdAt: sessionData.createdAt || now,
        updatedAt: now,
        transcription: sessionData.transcription || '',
        summary: sessionData.summary || null,
        diagnoses: sessionData.diagnoses || null,
        questions: sessionData.questions || null,
        manualNotes: sessionData.manualNotes || '',
        privateNotes: sessionData.privateNotes || '',
        conversationLog: sessionData.conversationLog || [],
        speakerNames: sessionData.speakerNames || {},
      };
      let next;
      if (existing >= 0) {
        next = [...prev];
        next[existing] = { ...next[existing], ...session };
      } else {
        next = [session, ...prev];
      }
      persistSessions(next);
      return next;
    });
  }, []);

  const getOpenSessions = useCallback(() => {
    return sessions.filter((s) => s.status === 'open').sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [sessions]);

  const getArchivedSessions = useCallback(() => {
    return sessions.filter((s) => s.status === 'archived').sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [sessions]);

  const loadSession = useCallback((id) => {
    return sessions.find((s) => s.id === id) || null;
  }, [sessions]);

  const deleteSession = useCallback((id) => {
    setSessions((prev) => {
      const next = prev.filter((s) => s.id !== id);
      persistSessions(next);
      return next;
    });
  }, []);

  return (
    <SessionContext.Provider value={{ sessions, saveSession, getOpenSessions, getArchivedSessions, loadSession, deleteSession }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSessions() {
  return useContext(SessionContext);
}
